import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { PrimaryButton, TextButton } from '../../styles/CommonStyles';
import { WORKSPACE_MARK_PALETTE } from '../../services/workspaceIdentity';
import { getAuth } from 'firebase/auth';
import { useRbac } from '../../hooks/useRbac';
import {
  listInvitations,
  listMembers,
  listRoles,
  removeMember,
  type InvitationSummary,
} from '../../services/rbacService';
import RoleAssignModal from './RoleAssignModal';
import InviteModal from './InviteModal';
import ConfirmDialog from './ConfirmDialog';
import WorkspaceRepairBanner from './WorkspaceRepairBanner';

/** Cards rendered before the "show more" button (bounded DOM for large orgs). */
const MEMBERS_PAGE_SIZE = 50;

const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  max-width: 340px;
  font-size: 0.8125rem;
  padding: 0.4375rem 0.625rem;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  color: #333;

  &::placeholder {
    color: var(--brand-text-muted);
  }
`;

const ShowMoreBtn = styled.button`
  display: block;
  margin: 1rem auto 0;
  font-size: 0.8125rem;
  padding: 0.4375rem 1.25rem;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  background: transparent;
  color: #555;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

// ── Types ────────────────────────────────────────────────────────────────────

interface AccountMember {
  userId: string;
  email: string;
  displayName?: string;
  status: 'active' | 'invited' | 'suspended' | 'removed';
  /** Contact e-mail from the member's profile page (source of truth for display; joined server-side). */
  profileEmail?: string | null;
  memberType: 'internal' | 'client-portal' | 'service-account';
  assignedRoleIds: string[];
  effectivePermissions: string[];
  isOwner: boolean;
  /** Address the accepted invitation was sent to (2026-08-12) — tagged below when it differs from the account email. */
  invitedEmail?: string | null;
}

/**
 * The address shown for a member: the profile page's contact e-mail is the
 * source of truth for display; the account (login/invite) e-mail is the
 * fallback and stays authoritative for provenance checks below.
 */
function displayEmail(member: AccountMember): string {
  return (member.profileEmail ?? '').trim() || (member.email ?? '');
}

/** True only when both addresses are known and genuinely differ. */
function joinedUnderDifferentEmail(member: AccountMember): boolean {
  const invited = (member.invitedEmail ?? '').trim().toLowerCase();
  const actual = (member.email ?? '').trim().toLowerCase();
  return !!invited && !!actual && invited !== actual;
}

// ── Styled components ────────────────────────────────────────────────────────

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const SectionLabel = styled.h3`
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin: 0;
  text-transform: lowercase;
`;

const MemberCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: white;
  border: 1px solid var(--brand-line);
  border-radius: 10px;
  margin-bottom: 0.75rem;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 600px) {
    flex-wrap: wrap;
  }
`;

const Avatar = styled.div<{ $color?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.$color ?? 'var(--brand-gold)'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
  flex-shrink: 0;
  text-transform: lowercase;
`;

const MemberInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const MemberName = styled.div`
  font-weight: 500;
  color: var(--brand-black);
  font-size: 0.9375rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MemberEmail = styled.div`
  font-size: 0.8125rem;
  color: var(--brand-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Badges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  align-items: center;
`;

const RoleBadge = styled.span`
  background: rgba(198, 167, 94, 0.1);
  color: #9a7c32;
  border: 1px solid rgba(198, 167, 94, 0.3);
  border-radius: 20px;
  padding: 0.125rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  text-transform: lowercase;
`;

const StatusBadge = styled.span<{ $status: string }>`
  border-radius: 20px;
  padding: 0.125rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  text-transform: lowercase;
  background: ${props => {
    switch (props.$status) {
      case 'active': return 'rgba(40, 167, 69, 0.1)';
      case 'invited': return 'rgba(255, 193, 7, 0.12)';
      case 'suspended': return 'rgba(220, 53, 69, 0.1)';
      default: return 'rgba(0,0,0,0.05)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'active': return '#28a745';
      case 'invited': return '#856404';
      case 'suspended': return '#dc3545';
      default: return '#666';
    }
  }};
`;

const OwnerLabel = styled.span`
  font-size: 0.75rem;
  color: var(--brand-text-muted);
  font-style: italic;
  white-space: nowrap;
`;

/* Provenance tag: the member accepted under a different account email than
   the invite was addressed to. Neutral styling — informational, not a
   warning (joining with another identity is a supported path). */
const InvitedAsBadge = styled.span`
  background: rgba(0, 0, 0, 0.04);
  color: var(--brand-text-muted);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 20px;
  padding: 0.125rem 0.625rem;
  font-size: 0.75rem;
  white-space: nowrap;
  text-transform: lowercase;
`;

/* "me" tag on the viewer's OWN row (2026-08-21 Miles) — the gold family and
   pill dimensions of the role chips so it belongs to the chip row, matching
   the "you" marker convention on the Activity by-member table. */
const MeBadge = styled.span`
  background: rgba(198, 167, 94, 0.16);
  color: #8a6d2a;
  border: 1px solid rgba(198, 167, 94, 0.4);
  border-radius: 20px;
  padding: 0.125rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  text-transform: lowercase;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const SmallBtn = styled(TextButton)`
  font-size: 0.8125rem;
  padding: 0.25rem 0.625rem;
`;

// Restrained danger (task #80): quiet outline in the scheme's #991B1B — the
// red the remove-confirm dialog already leads to — sized like its SmallBtn
// siblings, instead of the loud filled #f44336.
const SmallDangerBtn = styled(TextButton)`
  font-size: 0.8125rem;
  padding: 0.25rem 0.625rem;
  color: #991b1b;
  border: 1px solid rgba(153, 27, 27, 0.25);
  border-radius: 6px;

  &:hover {
    background: rgba(153, 27, 27, 0.06);
    color: #7f1d1d;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--brand-text-muted);
  background: white;
  border: 1px dashed #ddd;
  border-radius: 10px;

  p {
    margin: 0;
    font-size: 0.9375rem;
  }
`;

const ErrorState = styled.div`
  padding: 1.25rem;
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 8px;
  color: #c53030;
  font-size: 0.9rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// A member doc only exists once the invitee ACCEPTS, so an invited teammate is
// invisible on this page — the admin sends an invite, the list doesn't change,
// and they reasonably conclude the person is on the team. These rows close that
// gap and say plainly what is still outstanding.
const PendingNote = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.75rem 1rem;
  margin-bottom: 0.75rem;
  background: rgba(255, 193, 7, 0.08);
  border: 1px solid rgba(255, 193, 7, 0.35);
  border-radius: 8px;
  color: #7a5c00;
  font-size: 0.8125rem;
  line-height: 1.45;
`;

const PendingCard = styled(MemberCard)`
  border-style: dashed;
  background: #fcfbf7;
`;

// ── Helpers ──────────────────────────────────────────────────────────────────

// Owner directive 2026-08-18 (task #80): member avatars sit in the prest0
// scheme — the same muted, warm-anchored WORKSPACE_MARK_PALETTE the
// workspace dots use — never arbitrary full-spectrum hues.
const AVATAR_COLORS = WORKSPACE_MARK_PALETTE;

function avatarColor(userId: string | undefined | null): string {
  const safe = userId ?? '';
  let hash = 0;
  for (let i = 0; i < safe.length; i++) hash = safe.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function initials(member: AccountMember): string {
  if (member.displayName) {
    const parts = member.displayName.trim().split(/\s+/);
    return parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`
      : parts[0].slice(0, 2);
  }
  return (member.email ?? '??').slice(0, 2);
}

// ── Component ────────────────────────────────────────────────────────────────

interface MembersListProps {
  /** Jump to the invitations tab (resend / cancel). Absent hides the shortcut. */
  onManageInvitations?: () => void;
}

const MembersList: React.FC<MembersListProps> = ({ onManageInvitations }) => {
  const { hasPermission, isOwner } = useRbac();
  // The signed-in member (firebase uid == AccountMember.userId), for the "me"
  // tag. Same source the Activity by-member viewer highlight uses.
  const viewerUid = getAuth().currentUser?.uid ?? null;
  const [members, setMembers] = useState<AccountMember[]>([]);
  const [pendingInvites, setPendingInvites] = useState<InvitationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assignTarget, setAssignTarget] = useState<AccountMember | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<AccountMember | null>(null);
  // Voluntary exit (owner directive 2026-08-16): non-owners may leave the
  // organization themselves; the org keeps every thread (owners take them
  // over with full history) and this member's rail clears.
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState<string | null>(null);
  const [resolvedAccountId, setResolvedAccountId] = useState<string | null>(null);
  // Role NAMES, not raw ids — a badge reading "role_1719..." helps nobody.
  const [roleNames, setRoleNames] = useState<Record<string, string>>({});

  // #20: the three team.* capabilities are INDEPENDENT — an owner can grant
  // "can add" (team.invite) and "can remove" (team.remove) to different people.
  // Each affordance is gated on its OWN permission, not a combined check, so the
  // separation the reviewer asked for is real and visible.
  const canInvite = hasPermission('team.invite');
  const canRemove = hasPermission('team.remove');
  // GATE-MATCH FIX (ORG-USAGE-2026): rbacAssignRoles / rbacRevokeRole enforce
  // 'roles.assign' server-side. Gating this button on 'team.manage-roles' alone
  // meant a member could see "edit roles" and then fail at save, while someone
  // holding roles.assign never saw the button at all. Accept EITHER so existing
  // grants of team.manage-roles keep working, and the affordance now matches
  // what the server will actually allow.
  const canManageRoles = hasPermission('roles.assign') || hasPermission('team.manage-roles');

  // Large-org ergonomics (ORG-USAGE-2026 audit): client-side search + bounded
  // incremental rendering, so a 500-member workspace doesn't render 500 cards
  // and the admin can find a person by name, email, or role.
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(MEMBERS_PAGE_SIZE);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, rolesRes, invitesRes] = await Promise.all([
        listMembers(),
        listRoles().catch(() => ({ roles: [] as any[] })),
        // Non-fatal: pending invites are context. Losing them must never stop
        // the members list from rendering.
        listInvitations().catch(() => ({ invitations: [] as InvitationSummary[] })),
      ]);
      setMembers(data.members as AccountMember[]);
      setResolvedAccountId((data as any).resolvedAccountId ?? null);
      setPendingInvites(
        (invitesRes.invitations ?? []).filter(
          (i) => i.status === 'pending' || i.status === 'expired',
        ),
      );
      const nameMap: Record<string, string> = {};
      for (const role of (rolesRes as any).roles ?? []) {
        nameMap[role.roleId ?? role.id] = role.name ?? role.roleId ?? role.id;
      }
      setRoleNames(nameMap);
    } catch (err: any) {
      setError(err?.message ?? 'failed to load members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // §7.10 pause freeze: suspend/restore a membership. Confirmation is
  // deliberately light — pause is reversible by design (unlike remove).
  const [pausingUserId, setPausingUserId] = useState<string | null>(null);
  const handlePauseToggle = useCallback(async (member: AccountMember) => {
    const pausing = member.status !== 'suspended';
    // eslint-disable-next-line no-alert
    if (pausing && !window.confirm(
      `Pause ${member.displayName || displayEmail(member) || 'this member'}? Their access freezes `
      + '(threads stay visible to them read-only) until someone resumes them.',
    )) return;
    setPausingUserId(member.userId);
    setError(null);
    try {
      const { pauseMember, resumeMember } = await import('../../services/rbacService');
      if (pausing) await pauseMember(member.userId);
      else await resumeMember(member.userId);
      await load();
    } catch (err: any) {
      setError(err?.message ?? `failed to ${pausing ? 'pause' : 'resume'} member`);
    } finally {
      setPausingUserId(null);
    }
  }, [load]);

  const handleLeave = useCallback(async () => {
    if (!resolvedAccountId || leaving) return;
    setLeaving(true);
    setLeaveError(null);
    try {
      const { leaveOrganization } = await import('../../services/rbacService');
      await leaveOrganization(resolvedAccountId);
      const { clearWorkspaceOptionsCache } = await import('../../services/workspaceOptionsCache');
      clearWorkspaceOptionsCache();
      // The server flipped the back-pointer home if this was the active
      // workspace — the reload-in-place contract re-keys every surface.
      window.location.reload();
    } catch (err: any) {
      setLeaveError(err?.message ?? 'could not leave the organization');
      setLeaving(false);
    }
  }, [resolvedAccountId, leaving]);

  const handleRemove = useCallback(async () => {
    const member = removeTarget;
    if (!member) return;
    setRemovingUserId(member.userId);
    setError(null);
    try {
      await removeMember(member.userId);
      setRemoveTarget(null);
      await load();
    } catch (err: any) {
      // Server errors sometimes echo the target uid — a raw uid helps nobody.
      // Name the person the way the rest of the card does instead.
      const friendlyName = member.displayName || member.email || 'this member';
      const raw: string = err?.message ?? 'failed to remove member';
      setError(member.userId ? raw.split(member.userId).join(friendlyName) : raw);
    } finally {
      setRemovingUserId(null);
    }
  }, [load, removeTarget]);

  const filteredMembers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => {
      if ((m.displayName ?? '').toLowerCase().includes(q)) return true;
      if ((m.email ?? '').toLowerCase().includes(q)) return true;
      if ((m.profileEmail ?? '').toLowerCase().includes(q)) return true;
      return (m.assignedRoleIds ?? []).some((rid) =>
        (roleNames[rid] ?? rid).toLowerCase().includes(q)
      );
    });
  }, [members, searchQuery, roleNames]);

  const visibleMembers = filteredMembers.slice(0, visibleCount);

  if (loading) {
    return <div style={{ padding: '2rem 0', color: '#999', textAlign: 'center' }}>loading members…</div>;
  }

  return (
    <>
      {/* Detects the split-workspace defect and offers the repair. Renders nothing
          on a healthy workspace, so most admins never see it. */}
      <WorkspaceRepairBanner onRepaired={load} />

      <Header>
        <SectionLabel>
          {searchQuery.trim()
            ? `${filteredMembers.length} of ${members.length} members`
            : `${members.length} member${members.length !== 1 ? 's' : ''}` +
              (pendingInvites.length > 0 ? ` · ${pendingInvites.length} invited` : '')}
        </SectionLabel>
        {members.length > 8 && (
          <SearchInput
            type="search"
            placeholder="search by name, email, or role…"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setVisibleCount(MEMBERS_PAGE_SIZE);
            }}
            aria-label="search members"
          />
        )}
        {canInvite && (
          <PrimaryButton onClick={() => setInviteOpen(true)}>+ invite member</PrimaryButton>
        )}
      </Header>

      {error && (
        <ErrorState>
          <span>{error}</span>
          <SmallBtn onClick={load}>retry</SmallBtn>
        </ErrorState>
      )}

      {!error && pendingInvites.length > 0 && !searchQuery.trim() && (
        <PendingNote>
          <span>
            {pendingInvites.length === 1
              ? 'one person has been invited but hasn’t joined yet.'
              : `${pendingInvites.length} people have been invited but haven’t joined yet.`}{' '}
            they become full members — and can be added to threads — as soon as they accept.
          </span>
        </PendingNote>
      )}

      {!error && members.length === 0 && pendingInvites.length === 0 && (
        <EmptyState>
          <p>no members yet. invite someone to get started.</p>
        </EmptyState>
      )}

      {!error && members.length > 0 && filteredMembers.length === 0 && (
        <EmptyState>
          <p>nobody matches "{searchQuery}".</p>
        </EmptyState>
      )}

      {visibleMembers.map((member, idx) => (
        <MemberCard key={member.userId ?? `idx-${idx}`}>
          <Avatar $color={avatarColor(member.userId)}>
            {initials(member)}
          </Avatar>

          <MemberInfo>
            <MemberName>{member.displayName ?? displayEmail(member)}</MemberName>
            <MemberEmail>{displayEmail(member)}</MemberEmail>
          </MemberInfo>

          <Badges>
            {member.isOwner && <RoleBadge>owner</RoleBadge>}
            {/* Matches the owner story in the role editor: the owner always
                holds every permission, including new ones as they are added. */}
            {member.isOwner && <OwnerLabel>owner · always holds every permission</OwnerLabel>}
            {/* Roster-only viewers can't load role NAMES (roles.view) — a raw
                role id helps nobody, so the fallback is the plain word. */}
            {!member.isOwner && (member.assignedRoleIds ?? []).slice(0, 3).map(rid => (
              <RoleBadge key={rid}>{roleNames[rid] ?? 'member'}</RoleBadge>
            ))}
            {!member.isOwner && (member.assignedRoleIds ?? []).length > 3 && (
              <RoleBadge>+{(member.assignedRoleIds ?? []).length - 3}</RoleBadge>
            )}
            {joinedUnderDifferentEmail(member) && (
              <InvitedAsBadge
                title={`this member joined with ${member.email}, but the invitation was originally sent to ${member.invitedEmail}`}
              >
                originally invited as {member.invitedEmail}
              </InvitedAsBadge>
            )}
            {Boolean(viewerUid) && member.userId === viewerUid && <MeBadge>me</MeBadge>}
            <StatusBadge $status={member.status ?? 'active'}>{member.status ?? 'active'}</StatusBadge>
          </Badges>

          {!member.isOwner && (canManageRoles || canRemove) && (
            <Actions>
              {canManageRoles && (
                <SmallBtn onClick={() => setAssignTarget(member)}>edit roles</SmallBtn>
              )}
              {/* §7.10 pause freeze (owner decision 5): freeze instead of
                  ending — threads stay visible read-only until resumed. Same
                  team.remove gate and never on owner rows. */}
              {canRemove && (
                <SmallBtn
                  data-testid={`pause-resume-${member.userId}`}
                  disabled={pausingUserId === member.userId}
                  onClick={() => handlePauseToggle(member)}
                >
                  {pausingUserId === member.userId
                    ? 'saving…'
                    : member.status === 'suspended' ? 'resume' : 'pause'}
                </SmallBtn>
              )}
              {canRemove && (
                <SmallDangerBtn
                  onClick={() => setRemoveTarget(member)}
                  disabled={removingUserId === member.userId}
                >
                  {removingUserId === member.userId ? 'removing…' : 'remove'}
                </SmallDangerBtn>
              )}
            </Actions>
          )}
        </MemberCard>
      ))}

      {!searchQuery.trim() &&
        pendingInvites.map((invite) => (
          <PendingCard key={invite.inviteId}>
            <Avatar $color="#8F8440">
              {(invite.displayName || invite.email || '??').slice(0, 2)}
            </Avatar>

            <MemberInfo>
              <MemberName>{invite.displayName || invite.email}</MemberName>
              <MemberEmail>{invite.email}</MemberEmail>
            </MemberInfo>

            <Badges>
              {(invite.roleIds ?? []).slice(0, 3).map((rid) => (
                <RoleBadge key={rid}>{roleNames[rid] ?? rid}</RoleBadge>
              ))}
              <StatusBadge $status={invite.status === 'expired' ? 'suspended' : 'invited'}>
                {invite.status === 'expired' ? 'invite expired' : 'invited'}
              </StatusBadge>
            </Badges>

            {canInvite && onManageInvitations && (
              <Actions>
                <SmallBtn onClick={onManageInvitations}>manage invite</SmallBtn>
              </Actions>
            )}
          </PendingCard>
        ))}

      {filteredMembers.length > visibleCount && (
        <ShowMoreBtn onClick={() => setVisibleCount((c) => c + MEMBERS_PAGE_SIZE)}>
          show {Math.min(MEMBERS_PAGE_SIZE, filteredMembers.length - visibleCount)} more of{' '}
          {filteredMembers.length - visibleCount} remaining
        </ShowMoreBtn>
      )}

      {assignTarget && (
        <RoleAssignModal
          member={assignTarget}
          onClose={() => setAssignTarget(null)}
          onSaved={() => { setAssignTarget(null); load(); }}
        />
      )}

      <ConfirmDialog
        isOpen={!!removeTarget}
        error={removeTarget ? error : null}
        title="remove member"
        danger
        busy={removingUserId === removeTarget?.userId}
        confirmLabel="remove member"
        cancelLabel="keep them"
        message={`Remove ${removeTarget?.displayName || removeTarget?.email || 'this member'} from the team?`}
        consequence="They lose access to this workspace immediately. Their past work and activity history stay intact, and you can invite them back at any time."
        onConfirm={handleRemove}
        onCancel={() => setRemoveTarget(null)}
      />

      <InviteModal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSent={() => { setInviteOpen(false); load(); }}
      />

      {/* Voluntary exit (owner directive 2026-08-16): non-owners only — an
          organization's owner can never leave or remove themself. */}
      {!isOwner && resolvedAccountId && !loading && (
        <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #ECEAE4' }}>
          <TextButton
            data-testid="leave-organization-btn"
            style={{ color: '#9B2C2C' }}
            onClick={() => { setLeaveError(null); setLeaveOpen(true); }}
          >
            leave this organization…
          </TextButton>
        </div>
      )}
      <ConfirmDialog
        isOpen={leaveOpen}
        error={leaveError}
        title="leave this organization"
        danger
        busy={leaving}
        confirmLabel={leaving ? 'leaving…' : 'leave organization'}
        cancelLabel="stay"
        message="Leave this organization?"
        consequence={
          'Your threads in this organization stay WITH the organization — its owners take them ' +
          'over with full history — and they disappear from your view. Your account and your own ' +
          'organization are untouched. Rejoining later needs a new invitation.'
        }
        onConfirm={handleLeave}
        onCancel={() => setLeaveOpen(false)}
      />
    </>
  );
};

export default MembersList;
